import { useEffect } from 'react';
import Hero from './components/Hero';
import AboutUs from './components/AboutUs';
import ReasonsToEmpty from './components/ReasonsToEmpty';
import Services from './components/Services';
import WhyChooseUs from './components/WhyChooseUs';
import StepGuide from './components/StepGuide';
import CostEstimation from './components/CostEstimation';
import Preparation from './components/Preparation';
import ServiceAreas from './components/ServiceAreas';
import FAQ from './components/FAQ';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import ScrollToTop from '../../components/feature/ScrollToTop';

export default function HomePage() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vpb-barcelona.com';

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Vaciado de Pisos Barcelona VPB",
    "image": "https://www.mudanzasjuan.es/wp-content/uploads/2025/03/trabajadores-en-vaciado-de-un-piso.jpg",
    "description": "Servicio profesional de vaciado de pisos en Barcelona. Rápido, seguro y con precios transparentes. Más de 10 años de experiencia.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Barcelona",
      "addressRegion": "Cataluña",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.385064",
      "longitude": "2.173404"
    },
    "url": siteUrl,
    "telephone": "+34629501941",
    "priceRange": "€€",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "08:00",
        "closes": "20:00"
      }
    ],
    "areaServed": {
      "@type": "City",
      "name": "Barcelona"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "3"
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Vaciado de Pisos",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Vaciado de Pisos Barcelona VPB",
      "telephone": "+34629501941",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Barcelona",
        "addressRegion": "Cataluña",
        "addressCountry": "ES"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Barcelona"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Vaciado",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Vaciado completo",
            "description": "Retiramos todos los muebles, enseres y objetos del piso de forma profesional y eficiente."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Gestión de residuos",
            "description": "Nos encargamos del reciclaje y eliminación responsable de todos los materiales."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Transporte incluido",
            "description": "Disponemos de vehículos propios para el traslado de todos los enseres."
          }
        }
      ]
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": siteUrl
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Cuánto tiempo toma vaciar un piso?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "El tiempo depende del tamaño del piso y la cantidad de objetos. Generalmente, un piso estándar de 60-80m² se puede vaciar en 4-8 horas. Para pisos más grandes o con mucho contenido, puede tomar entre 1 y 2 días. Siempre nos adaptamos a tus necesidades y plazos."
        }
      },
      {
        "@type": "Question",
        "name": "¿Qué objetos se pueden retirar en un vaciado de piso?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Retiramos todo tipo de objetos: muebles (sofás, armarios, camas, mesas), electrodomésticos (neveras, lavadoras, hornos), ropa, libros, enseres personales, escombros, residuos en general. Si hay objetos especiales o peligrosos, consúltanos previamente."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto cuesta vaciar un piso en Barcelona?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "El precio varía según el tamaño del piso, cantidad de objetos, accesibilidad y servicios adicionales. Los precios suelen empezar desde 200€ para pisos pequeños. Te ofrecemos un presupuesto gratuito y sin compromiso tras evaluar tu caso específico."
        }
      },
      {
        "@type": "Question",
        "name": "¿Tengo que estar presente durante el vaciado?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No es imprescindible. Si contratas nuestro servicio completo y nos das las indicaciones necesarias, nuestro equipo profesional puede realizar el trabajo sin tu presencia. Contamos con seguro de responsabilidad civil que cubre cualquier incidencia."
        }
      },
      {
        "@type": "Question",
        "name": "¿Qué pasa con los objetos retirados?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Gestionamos todos los residuos de forma responsable y legal. Los objetos en buen estado se donan a entidades benéficas, los materiales reciclables se llevan a puntos limpios autorizados, y el resto se elimina siguiendo la normativa medioambiental vigente."
        }
      }
    ]
  };

  return (
    <>
      <title>Vaciado de Pisos Barcelona | Servicio Profesional y Rápido - VPB</title>
      <meta name="description" content="Servicio profesional de vaciado de pisos en Barcelona. Rápido, seguro y con precios transparentes. Más de 10 años de experiencia. ¡Solicita presupuesto gratis!" />
      <meta name="keywords" content="vaciado de pisos Barcelona, vaciado de pisos, limpieza de pisos, recogida de muebles Barcelona, gestión de residuos" />
      <link rel="canonical" href={siteUrl} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      
      <div className="min-h-screen bg-white overflow-x-hidden">
        <Navbar />
        <ScrollToTop />
        <Hero />
        <AboutUs />
        <ReasonsToEmpty />
        <Services />
        <WhyChooseUs />
        <StepGuide />
        <CostEstimation />
        <Preparation />
        <ServiceAreas />
        <FAQ />
        <Testimonials />
        <Contact />
        <Footer />
      </div>
    </>
  );
}